import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import {
    Address,
    AddressWithApartments,
    AirbnbReview,
    ApartmentGroup,
    ApartmentOwner,
    ApartmentStats,
    BltStatsItem,
    BookingPaceItem,
    Convert,
    ExpenseCategory,
    GetReviewPerformanceResponse,
    MunicipalitySection,
    MunicipalitySectionInUseError,
    MunicipalitySectionWithCount,
    OccupancyItem,
    GetReservationDigestResponse,
    OwnerGroup,
    OwnerGroupInUseError,
    OwnerGroupWithCount,
    PayoutStat,
    Tag,
    TagInUseError,
    TagWithCount,
} from "@/models/responses";
import { AddressInUseError } from "@/utils/inUseError";
import { formatDateToISODate } from "@/utils/dates";
import { apiUrl } from "@/layouts/apiUrl";

export interface MCPAPIKey {
    id: number;
    name: string;
    createdAt: string;
    lastUsedAt: string | null;
    isActive: boolean;
}

export interface CreatedMCPAPIKey extends MCPAPIKey {
    key: string;
}

/**
 * Wire payload for the admin "add reservation" endpoint
 * (`POST /api/admin/reservations`). Mirrors the Go binding in
 * `analytics/cmd/api/requests/reservations.go` `AddReservationRequest`.
 *
 * Hand-typed (rather than generated) because this is a *request*: dates are
 * serialised as `YYYY-MM-DD` strings and decimals as numeric STRINGS (e.g.
 * "142.00") so JS float drift never reaches shopspring's decimal parser.
 *
 * Deliberately omitted: `origin` / `changeSource` (the backend stamps them
 * "manual" authoritatively) and `epsilon` (defaults false; manual adds are not
 * pre-uploaded to MyData).
 */
export interface AddReservationPayload {
    reservationCode: string;
    apartmentId: number;
    platform: number;
    bookingDate: string;
    startDate: string;
    endDate: string;
    actualDaysOfStay: number;
    guestName: string;
    guestId?: string;
    numberOfGuests: number;
    receivableIncome: string;
    amountPaid?: string;
    paymentDate?: string;
    commission: string;
    cleaningFee?: string;
    cleaningCost?: string;
    hostingExpense?: string;
}

/**
 * Wire payload for the manual reservation override
 * (`PATCH /api/admin/reservations/{id}`), mirroring `PatchReservationRequest` in
 * `analytics/cmd/api/responses/types.go`.
 *
 * Every reservation field is optional: an override submits ONLY what it changes
 * and the server carries the rest forward (manual-reservation-override/FR-003).
 * `changeReason` is the one required member (FR-006).
 *
 * Money stays a decimal STRING and dates are RFC3339 — a `time.Time` bound from
 * a JSON body only parses RFC3339, and a float round-trip on a payout-bearing
 * field is where a cent of drift starts.
 */
export interface OverrideReservationPayload {
    startDate?: string;
    endDate?: string;
    actualDaysOfStay?: number;
    guestName?: string;
    numberOfGuests?: number;
    receivableIncome?: string;
    commission?: string;
    cleaningFee?: string;
    cleaningCost?: string;
    // Voids the booking, or lifts a void when false. Rides on the same request as
    // the facts so voiding and zeroing the money is one atomic change
    // (manual-reservation-cancellation/FR-006).
    isCancelled?: boolean;
    changeReason: string;
}

/**
 * The body of `POST /admin/addresses` (address-normalization/FR-001, /FR-011) —
 * the same four fields whether the building is registered from the management
 * page or inline from an apartment form. `municipalitySectionId` is required:
 * the section now hangs off the address, not the apartment (FR-008).
 */
export interface AddAddressPayload {
    street: string;
    number: string;
    postalCode: string;
    municipalitySectionId: number;
}

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
    const basePath = window.location.pathname.startsWith("/demo") ? "/api/demo/" : "/api/";
    return fetchBaseQuery({
        baseUrl: `${apiUrl}${basePath}`,
        credentials: "include",
    })(args, api, extraOptions);
};

export const bookingManagementApi = createApi({
    reducerPath: "bookingManagementApi",
    baseQuery: dynamicBaseQuery,
    tagTypes: [
        "BookingPace",
        "Occupancies",
        "Owners",
        "Apartments",
        "Groups",
        "ExpenseCategories",
        "Years",
        "BltStats",
        "PayoutStats",
        "ApartmentStats",
        "Reservations",
        "Tags",
        "TagsWithCounts",
        "Addresses",
        "AddressesWithCounts",
        "MunicipalitySections",
        "MunicipalitySectionsWithCounts",
        "OwnerGroups",
        "OwnerGroupsWithCounts",
        "AirbnbReviews",
        "MCPAPIKeys",
        "NeedsRefile",
        "ReservationDigest",
        "HostHubSyncIssues",
        "NeteraAccounts",
    ],
    endpoints: (builder) => ({
        getBookingPace: builder.query<BookingPaceItem[], { startDate: string; endDate: string }>({
            query: ({ startDate, endDate }) => ({
                url: `admin/pace`,
                params: { startDate, endDate },
            }),
            transformResponse: (response: string) => {
                const data = Convert.toGetBookingPaceResponse(JSON.stringify(response));
                return data?.bookingPace || [];
            },
            providesTags: ["BookingPace"], // Add cache tag
        }),
        getBltStats: builder.query<BltStatsItem[], { period: string; year: string }>({
            query: ({ period, year }) => ({
                url: `admin/blt`,
                params: { period, year },
            }),
            transformResponse: (response: string) => {
                const data = Convert.toGetBltStatsResponse(JSON.stringify(response));
                return data?.bltStats || [];
            },
            providesTags: ["BltStats"], // Add cache tag
        }),
        getOccupancies: builder.query<
            OccupancyItem[],
            { date: string; apartmentId: number; groupId: number; apartmentIds?: number[] }
        >({
            query: ({ date, apartmentId, groupId, apartmentIds }) => {
                const params = new URLSearchParams({ date: date.toString() });
                // An explicit `apartmentIds=1,2,3` set makes the backend aggregate
                // over that set instead of branching on apartmentId/groupId — how the
                // shared apartment filters are honoured (FR-005a/b/c,
                // apartment-filters-graphs-and-database).
                if (apartmentIds && apartmentIds.length > 0) {
                    params.append("apartmentIds", apartmentIds.join(","));
                }
                if (apartmentId && apartmentId !== 0) {
                    params.append("apartmentId", apartmentId.toString());
                }
                if (groupId && groupId !== 0) {
                    params.append("groupId", groupId.toString());
                }
                return {
                    url: `admin/occupancies?${params.toString()}`,
                };
            },
            transformResponse: (response: string) => {
                const data = Convert.toGetOccupanciesResponse(JSON.stringify(response));
                return data?.occupancies || [];
            },
            providesTags: ["Occupancies"], // Add cache tag
        }),
        // Owner-scoped variants of the three admin dashboards (Booking Pace,
        // BLT, Occupancy). Server-side scoping to the owner's apartment set
        // (visibleOwnerFilter on the :ownerId path param); the only wire
        // difference from the admin endpoints is the `owners/${ownerId}/`
        // prefix + an `ownerId` in the query arg. Response shapes are identical
        // so the existing chart components reuse unchanged.
        getOwnerBookingPace: builder.query<BookingPaceItem[], { ownerId: number; startDate: string; endDate: string }>({
            query: ({ ownerId, startDate, endDate }) => ({
                url: `owners/${ownerId}/pace`,
                params: { startDate, endDate },
            }),
            transformResponse: (response: string) => {
                const data = Convert.toGetBookingPaceResponse(JSON.stringify(response));
                return data?.bookingPace || [];
            },
            providesTags: ["BookingPace"],
        }),
        getOwnerBltStats: builder.query<BltStatsItem[], { ownerId: number; period: string; year: string }>({
            query: ({ ownerId, period, year }) => ({
                url: `owners/${ownerId}/blt`,
                params: { period, year },
            }),
            transformResponse: (response: string) => {
                const data = Convert.toGetBltStatsResponse(JSON.stringify(response));
                return data?.bltStats || [];
            },
            providesTags: ["BltStats"],
        }),
        getOwnerOccupancies: builder.query<
            OccupancyItem[],
            { ownerId: number; date: string; apartmentId: number }
        >({
            query: ({ ownerId, date, apartmentId }) => {
                const params = new URLSearchParams({ date: date.toString() });
                // A non-zero apartmentId narrows to that single apartment
                // (intersected server-side with the owner's set); omitting it
                // returns the owner's whole portfolio. groupId is not applicable
                // to owner scope and is never sent.
                if (apartmentId && apartmentId !== 0) {
                    params.append("apartmentId", apartmentId.toString());
                }
                return {
                    url: `owners/${ownerId}/occupancies?${params.toString()}`,
                };
            },
            // Owner backend returns the identical GetOccupanciesResponse shape as
            // the admin endpoint, so this mirrors the admin `getOccupancies` cast —
            // strict `Convert` validation, no silent defaulting of day-count fields.
            transformResponse: (response: string) => {
                const data = Convert.toGetOccupanciesResponse(JSON.stringify(response));
                return data?.occupancies || [];
            },
            providesTags: ["Occupancies"],
        }),

        getYearsInOperation: builder.query<number[], boolean>({
            query: (isAdmin) => ({
                url: `${isAdmin ? "admin/" : ""}stats/years`,
            }),
            transformResponse: (response: string) => {
                const data = Convert.toGetYearsInOperationResponse(JSON.stringify(response));
                return data.years.sort((a, b) => a - b);
            },
            providesTags: ["Years"],
        }),

        getApartmentOwners: builder.query<ApartmentOwner[], boolean>({
            query: (isAdmin) => ({
                url: `${isAdmin ? "admin/" : ""}owners`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetApartmentOwnersResponse(JSON.stringify(response)).owners;
            },
            providesTags: ["Owners"],
        }),
        updateApartmentOwner: builder.mutation<void, ApartmentOwner>({
            query: ({ id, fullName, taxId, taxisUserName, taxisPassword }) => ({
                url: `admin/owners/${id}`,
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: { fullName, taxisUserName, taxisPassword, taxId },
            }),
            invalidatesTags: ["Owners"],
        }),
        addApartmentOwner: builder.mutation<void, ApartmentOwner>({
            query: ({ fullName, taxisUserName, taxisPassword, taxId }) => ({
                url: "admin/owners",
                method: "POST",
                body: { fullName, taxisUserName, taxisPassword, taxId },
            }),
            invalidatesTags: ["Owners"],
        }),

        getApartmentStats: builder.query<ApartmentStats[], { startDate: Date; endDate: Date }>({
            query: ({ startDate, endDate }) => ({
                url: `admin/apartments/stats?startDate=${formatDateToISODate(startDate)}&endDate=${formatDateToISODate(endDate)}`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetStatsForApartmentsResponse(JSON.stringify(response)).apartmentStats;
            },
            providesTags: ["ApartmentStats"],
        }),

        getPayouts: builder.query<PayoutStat[], { startDate: Date; endDate: Date }>({
            query: ({ startDate, endDate }) => ({
                url: `admin/apartments/payouts?startDate=${formatDateToISODate(startDate)}&endDate=${formatDateToISODate(endDate)}`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetPayoutStatsResponse(JSON.stringify(response)).payoutStats;
            },
            providesTags: ["PayoutStats"],
        }),

        getGroups: builder.query<ApartmentGroup[], void>({
            query: () => ({
                url: "admin/groups",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetApartmentGroupsResponse(JSON.stringify(response)).groups;
            },
            providesTags: ["Groups"],
        }),

        getExpenseCategories: builder.query<ExpenseCategory[], void>({
            query: () => ({
                url: "expense-categories",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetExpenseCategoriesResponse(JSON.stringify(response)).expenseCategories;
            },
            providesTags: ["ExpenseCategories"],
        }),

        setReservationStrStatus: builder.mutation<void, { uploaded: boolean; reservationId: string }>({
            query: ({ uploaded, reservationId }) => ({
                url: `admin/reservations/${reservationId}/str-status`,
                method: "PUT",
                body: { uploaded },
            }),
            invalidatesTags: ["Reservations"],
        }),

        // Manual add of a single reservation (e.g. a booking that predates
        // HostHub sync). The backend computes taxes/VAT/payout itself from the
        // raw inputs here; the form only creates the row (MyData invoicing is a
        // separate, explicit grid action afterwards).
        addReservation: builder.mutation<void, AddReservationPayload>({
            query: (payload) => ({
                url: "admin/reservations",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["Reservations", "ReservationDigest"],
        }),

        // Manual override of an existing reservation
        // (manual-reservation-override/FR-024). Only the CHANGED fields travel;
        // the server carries the rest forward (FR-003). Decimals are strings for
        // the same reason `AddReservationPayload` uses them.
        overrideReservation: builder.mutation<void, { reservationId: number; patch: OverrideReservationPayload }>({
            query: ({ reservationId, patch }) => ({
                url: `admin/reservations/${reservationId}`,
                method: "PATCH",
                body: patch,
            }),
            invalidatesTags: ["Reservations", "ReservationDigest"],
        }),

        // Hand a frozen reservation back to HostHub sync
        // (manual-reservation-override/FR-012, FR-027).
        resumeReservationSync: builder.mutation<void, number>({
            query: (reservationId) => ({
                url: `admin/reservations/${reservationId}/resume-sync`,
                method: "POST",
            }),
            invalidatesTags: ["Reservations", "ReservationDigest"],
        }),

        getReservationDigest: builder.query<GetReservationDigestResponse, { startDate: string; endDate: string }>({
            query: ({ startDate, endDate }) => ({
                url: `admin/reservations/digest?startDate=${startDate}&endDate=${endDate}`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetReservationDigestResponse(JSON.stringify(response));
            },
            providesTags: ["ReservationDigest"],
        }),

        /**
         * The registered buildings an apartment can be given
         * (address-normalization/FR-009). The apartment forms hold the whole list
         * client-side: it is small, and the combobox filters it locally with the
         * identity fold rather than round-tripping every keystroke.
         */
        getAddresses: builder.query<Address[], void>({
            query: () => ({
                url: "admin/addresses",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetAddressesResponse(JSON.stringify(response)).addresses;
            },
            providesTags: ["Addresses"],
        }),
        /**
         * The registered buildings with the apartments standing at each
         * (address-normalization/AS-008). Backs the management page, which has to
         * be able to NAME the occupants of a building, not just count them — and
         * the same list is what the delete guard blocks on, so a building that
         * lists no apartments here is exactly one that can be removed.
         *
         * In-service apartments only: a decommissioned apartment neither appears
         * nor blocks.
         */
        getAddressesWithCounts: builder.query<AddressWithApartments[], void>({
            query: () => ({
                url: "admin/addresses/with-counts",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetAddressesWithCountsResponse(JSON.stringify(response)).addresses;
            },
            providesTags: ["AddressesWithCounts"],
        }),
        /**
         * Register a building (address-normalization/FR-011). Reached from the
         * apartment form's inline create as well as the management page.
         *
         * It deliberately does NOT invalidate `Addresses`: the response carries
         * the created building, so the cached list is patched in place. A refetch
         * here would be a second request for something already in hand, and the
         * form needs the new option present the instant it selects it.
         *
         * `AddressesWithCounts` IS invalidated: that projection carries the
         * apartments standing at each building, which the create response does not
         * know about, so it cannot be patched from the response alone.
         */
        addAddress: builder.mutation<Address, AddAddressPayload>({
            query: (payload) => ({
                url: "admin/addresses",
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: { address: Address }) => {
                return response.address;
            },
            onQueryStarted: async (_payload, { dispatch, queryFulfilled }) => {
                const { data: created } = await queryFulfilled;
                dispatch(
                    bookingManagementApi.util.updateQueryData("getAddresses", undefined, (addresses) => {
                        if (!addresses.some((address) => address.id === created.id)) {
                            addresses.push(created);
                        }
                    }),
                );
            },
            invalidatesTags: ["AddressesWithCounts"],
        }),
        /**
         * Correct a building in place (address-normalization/AS-022, /FR-002,
         * /FR-003). The body is the same four fields as creation: the server
         * re-folds the identity and refuses an edit that collides with another
         * building, so a typo fix and a refiling travel the same path.
         *
         * Unlike `addAddress` this DOES invalidate `Addresses`: an edit can change
         * the street, number or postal code, so every cached copy of the building —
         * including the one the apartment forms' dropdown is showing — is stale.
         * `Apartments` is invalidated too, because an apartment derives its
         * municipality section from its building (FR-020), so refiling the building
         * changes what every apartment at it reports.
         */
        updateAddress: builder.mutation<Address, { id: number } & AddAddressPayload>({
            query: ({ id, ...payload }) => ({
                url: `admin/addresses/${id}`,
                method: "PUT",
                body: payload,
            }),
            transformResponse: (response: { address: Address }) => {
                return response.address;
            },
            invalidatesTags: ["Addresses", "AddressesWithCounts", "Apartments"],
        }),
        /**
         * Remove a building (address-normalization/AS-006, /AS-007). A 409 carries
         * the in-service apartments still standing at it, so the refusal can name
         * them rather than leaving the operator with a bare "in use".
         */
        deleteAddress: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `admin/addresses/${id}`,
                method: "DELETE",
            }),
            transformErrorResponse: (error: FetchBaseQueryError) => {
                if (error.status === 409 && typeof error.data === "object" && error.data !== null) {
                    return error.data as AddressInUseError;
                }
                return error;
            },
            invalidatesTags: ["Addresses", "AddressesWithCounts", "Apartments"],
        }),
        getMunicipalitySections: builder.query<MunicipalitySection[], void>({
            query: () => ({
                url: "municipality-sections",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetMunicipalitySectionsResponse(JSON.stringify(response)).municipalitySections;
            },
            providesTags: ["MunicipalitySections"],
        }),
        getMunicipalitySectionsWithCounts: builder.query<MunicipalitySectionWithCount[], void>({
            query: () => ({
                url: "admin/municipality-sections/with-counts",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetMunicipalitySectionsWithCountsResponse(JSON.stringify(response))
                    .municipalitySections;
            },
            providesTags: ["MunicipalitySectionsWithCounts"],
        }),
        addMunicipalitySection: builder.mutation<MunicipalitySection, { name: string }>({
            query: ({ name }) => ({
                url: "admin/municipality-sections",
                method: "POST",
                body: { name },
            }),
            transformResponse: (response: { municipalitySection: MunicipalitySection }) => {
                return response.municipalitySection;
            },
            invalidatesTags: ["MunicipalitySections", "MunicipalitySectionsWithCounts"],
        }),
        updateMunicipalitySection: builder.mutation<MunicipalitySection, { id: number; name: string }>({
            query: ({ id, name }) => ({
                url: `admin/municipality-sections/${id}`,
                method: "PUT",
                body: { name },
            }),
            transformResponse: (response: { municipalitySection: MunicipalitySection }) => {
                return response.municipalitySection;
            },
            invalidatesTags: ["MunicipalitySections", "MunicipalitySectionsWithCounts"],
        }),
        deleteMunicipalitySection: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `admin/municipality-sections/${id}`,
                method: "DELETE",
            }),
            transformErrorResponse: (error: FetchBaseQueryError) => {
                if (error.status === 409 && typeof error.data === "object" && error.data !== null) {
                    try {
                        return Convert.toMunicipalitySectionInUseError(
                            JSON.stringify(error.data),
                        ) as MunicipalitySectionInUseError;
                    } catch {
                        /* fall through */
                    }
                }
                return error;
            },
            invalidatesTags: ["MunicipalitySections", "MunicipalitySectionsWithCounts", "Apartments"],
        }),
        getOwnerGroups: builder.query<OwnerGroup[], void>({
            query: () => ({
                url: "owner-groups",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetOwnerGroupsResponse(JSON.stringify(response)).ownerGroups;
            },
            providesTags: ["OwnerGroups"],
        }),
        getOwnerGroupsWithCounts: builder.query<OwnerGroupWithCount[], void>({
            query: () => ({
                url: "admin/owner-groups/with-counts",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetOwnerGroupsWithCountsResponse(JSON.stringify(response)).ownerGroups;
            },
            providesTags: ["OwnerGroupsWithCounts"],
        }),
        addOwnerGroup: builder.mutation<OwnerGroup, { name: string; monthlyFee: number }>({
            query: ({ name, monthlyFee }) => ({
                url: "admin/owner-groups",
                method: "POST",
                body: { name, monthlyFee },
            }),
            transformResponse: (response: { ownerGroup: OwnerGroup }) => {
                return response.ownerGroup;
            },
            invalidatesTags: ["OwnerGroups", "OwnerGroupsWithCounts"],
        }),
        updateOwnerGroup: builder.mutation<OwnerGroup, { id: number; name: string; monthlyFee: number }>({
            query: ({ id, name, monthlyFee }) => ({
                url: `admin/owner-groups/${id}`,
                method: "PUT",
                body: { name, monthlyFee },
            }),
            transformResponse: (response: { ownerGroup: OwnerGroup }) => {
                return response.ownerGroup;
            },
            invalidatesTags: ["OwnerGroups", "OwnerGroupsWithCounts"],
        }),
        deleteOwnerGroup: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `admin/owner-groups/${id}`,
                method: "DELETE",
            }),
            transformErrorResponse: (error: FetchBaseQueryError) => {
                if (error.status === 409 && typeof error.data === "object" && error.data !== null) {
                    try {
                        return Convert.toOwnerGroupInUseError(JSON.stringify(error.data)) as OwnerGroupInUseError;
                    } catch {
                        /* fall through */
                    }
                }
                return error;
            },
            invalidatesTags: ["OwnerGroups", "OwnerGroupsWithCounts", "Apartments"],
        }),

        getAllTags: builder.query<Tag[], void>({
            query: () => ({
                url: "admin/tags",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetAllTagsResponse(JSON.stringify(response)).tags;
            },
            providesTags: ["Tags"],
        }),

        addTag: builder.mutation<Tag, { name: string }>({
            query: ({ name }) => ({
                url: "admin/tags",
                method: "POST",
                body: { name },
            }),
            transformResponse: (response: string) => {
                return Convert.toAddTagResponse(JSON.stringify(response)).tag;
            },
            invalidatesTags: ["Tags", "TagsWithCounts"],
        }),

        getTagsWithCounts: builder.query<TagWithCount[], void>({
            query: () => ({
                url: "admin/tags/with-counts",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetTagsWithCountsResponse(JSON.stringify(response)).tags;
            },
            providesTags: ["TagsWithCounts"],
        }),

        updateTag: builder.mutation<Tag, { id: number; name: string }>({
            query: ({ id, name }) => ({
                url: `admin/tags/${id}`,
                method: "PUT",
                body: { name },
            }),
            transformResponse: (response: string) => {
                return Convert.toAddTagResponse(JSON.stringify(response)).tag;
            },
            invalidatesTags: ["Tags", "TagsWithCounts", "Apartments"],
        }),

        deleteTag: builder.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: `admin/tags/${id}`,
                method: "DELETE",
            }),
            transformErrorResponse: (error: FetchBaseQueryError) => {
                if (error.status === 409 && typeof error.data === "object" && error.data !== null) {
                    try {
                        return Convert.toTagInUseError(JSON.stringify(error.data)) as TagInUseError;
                    } catch {
                        /* fall through */
                    }
                }
                return error;
            },
            invalidatesTags: ["Tags", "TagsWithCounts", "Apartments"],
        }),

        getLatestAirbnbReviews: builder.query<AirbnbReview[], void>({
            query: () => ({
                url: "admin/airbnb-reviews/latest",
                method: "GET",
            }),
            transformResponse: (response: string) => {
                return Convert.toGetAirbnbReviewsResponse(JSON.stringify(response)).reviews;
            },
            providesTags: ["AirbnbReviews"],
        }),

        getHistoricalAirbnbReviews: builder.query<AirbnbReview[], { startDate: string; endDate: string }>({
            query: ({ startDate, endDate }) => ({
                url: "admin/airbnb-reviews/history",
                method: "GET",
                params: { startDate, endDate },
            }),
            transformResponse: (response: string) => {
                return Convert.toGetAirbnbReviewsResponse(JSON.stringify(response)).reviews;
            },
            providesTags: ["AirbnbReviews"],
        }),

        getReviewPerformance: builder.query<GetReviewPerformanceResponse, { year: string; period: string }>({
            query: ({ year, period }) => ({
                url: "admin/airbnb-reviews/performance",
                method: "GET",
                params: { year, period },
            }),
            transformResponse: (response: string) => {
                return Convert.toGetReviewPerformanceResponse(JSON.stringify(response));
            },
            providesTags: ["AirbnbReviews"],
        }),

        updateAirbnbReviews: builder.mutation<void, void>({
            query: () => ({
                url: "admin/airbnb-reviews/update",
                method: "POST",
                body: {},
            }),
            invalidatesTags: ["AirbnbReviews"],
        }),

        getMCPAPIKeys: builder.query<MCPAPIKey[], void>({
            query: () => ({
                url: "admin/mcp-api-keys",
            }),
            transformResponse: (response: { apiKeys: MCPAPIKey[] }) => {
                return response.apiKeys;
            },
            providesTags: ["MCPAPIKeys"],
        }),

        createMCPAPIKey: builder.mutation<CreatedMCPAPIKey, { name: string }>({
            query: ({ name }) => ({
                url: "admin/mcp-api-keys",
                method: "POST",
                body: { name },
            }),
            transformResponse: (response: { apiKey: CreatedMCPAPIKey }) => {
                return response.apiKey;
            },
            invalidatesTags: ["MCPAPIKeys"],
        }),

        deactivateMCPAPIKey: builder.mutation<void, number>({
            query: (id) => ({
                url: `admin/mcp-api-keys/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["MCPAPIKeys"],
        }),
    }),
});

export const {
    useGetBookingPaceQuery,
    useGetOwnerBookingPaceQuery,
    useGetOwnerBltStatsQuery,
    useGetOwnerOccupanciesQuery,
    useGetOccupanciesQuery,
    useGetYearsInOperationQuery,
    useGetApartmentOwnersQuery,
    useGetGroupsQuery,
    useGetExpenseCategoriesQuery,
    useGetBltStatsQuery,
    useAddApartmentOwnerMutation,
    useUpdateApartmentOwnerMutation,
    useGetPayoutsQuery,
    useGetApartmentStatsQuery,
    useGetReservationDigestQuery,
    useGetAddressesQuery,
    useGetAddressesWithCountsQuery,
    useAddAddressMutation,
    useUpdateAddressMutation,
    useDeleteAddressMutation,
    useGetMunicipalitySectionsQuery,
    useGetMunicipalitySectionsWithCountsQuery,
    useAddMunicipalitySectionMutation,
    useUpdateMunicipalitySectionMutation,
    useDeleteMunicipalitySectionMutation,
    useGetOwnerGroupsQuery,
    useGetOwnerGroupsWithCountsQuery,
    useAddOwnerGroupMutation,
    useUpdateOwnerGroupMutation,
    useDeleteOwnerGroupMutation,
    useSetReservationStrStatusMutation,
    useAddReservationMutation,
    useOverrideReservationMutation,
    useResumeReservationSyncMutation,
    useGetAllTagsQuery,
    useAddTagMutation,
    useGetTagsWithCountsQuery,
    useUpdateTagMutation,
    useDeleteTagMutation,
    useGetLatestAirbnbReviewsQuery,
    useGetHistoricalAirbnbReviewsQuery,
    useGetReviewPerformanceQuery,
    useUpdateAirbnbReviewsMutation,
    useGetMCPAPIKeysQuery,
    useCreateMCPAPIKeyMutation,
    useDeactivateMCPAPIKeyMutation,
} = bookingManagementApi;
